import { Box, Tooltip } from '@chakra-ui/react';
import TimeAgo from 'react-timeago';
import { formatDate } from '@/lib/date';
import enShort from 'react-timeago/lib/language-strings/en';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';
import { format } from 'date-fns';
import { selectUser } from '@/store/bootstrap/selectors';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { KText } from '@/ui-library/components/text';
import { useKFlags } from '@/hooks/use-k-flags';
import { getDateFormat } from '@/hooks/useDateFormat';

const formatter = buildFormatter({
  ...enShort,
  suffixAgo: '',
  suffixFromNow: '',
  minute: 'a minute',
  minutes: '%d minutes',
  hour: 'an hour',
  hours: '%d hours',
  day: 'a day',
  days: '%d days',
  month: 'a month',
  months: '%d months',
  year: 'a year',
  years: '%d years',
});

const customFormatter = (value, unit, suffix, epochSeconds, nextFormatter) => {
  const suffixFixed = '';
  if (unit === 'day') {
    return value === 1 ? `1 Day${suffixFixed}` : `${value} Days${suffixFixed}`;
  }
  return `${value} ${unit}${suffixFixed}`;
};

export const Time = ({
  time,
  isAts = false,
  isTaskRevamp = false,
  ...props
}) => {
  const currUser = useSelector(selectUser);
  const { ats_enabled } = useKFlags();
  const { time_fmt, date_fmt } = currUser || {};

  if (!time)
    return (
      <KText size="sm-medium" {...props}>
        -
      </KText>
    );

  const formatTime = (dateString) => {
    if (ats_enabled && time_fmt && date_fmt) {
      const date = new Date(dateString);
      const timeFormat = time_fmt === 'hour_12' ? 'hh:mm a' : 'HH:mm';
      const dateFormat = getDateFormat(date_fmt);
      return format(date, `${dateFormat} ${timeFormat}`);
    }
    return formatDate(dateString);
  };

  return (
    <Tooltip
      label={formatTime(time)}
      borderRadius="4px"
      placement="top-start"
      mb="2px"
      mr="30px"
      shouldWrapChildren
      variant={'default'}
    >
      <KText size="sm-medium" color="secondary-700" {...props}>
        <TimeAgo
          date={time}
          live={false}
          title={null}
          color="secondary-700"
          {...(isTaskRevamp ? { formatter: customFormatter } : {})}
        />
      </KText>
    </Tooltip>
  );
};

export const ShortTime = ({ time, ...props }) => {
  const currUser = useSelector(selectUser);
  const { ats_enabled } = useKFlags();
  const { time_fmt, date_fmt } = currUser || {};

  const formatTime = (dateString) => {
    if (ats_enabled && time_fmt && date_fmt) {
      const date = new Date(dateString);
      const timeFormat = time_fmt === 'hour_12' ? 'hh:mm a' : 'HH:mm';
      const dateFormat = getDateFormat(date_fmt);
      return format(date, `${dateFormat} ${timeFormat}`);
    }
    return formatDate(dateString);
  };

  const customFormatter = (value, unit, suffix) => {
    if (ats_enabled && time_fmt) {
      const date = new Date(time);
      const timeFormat = time_fmt === 'hour_12' ? 'hh:mm a' : 'HH:mm';
      return format(date, timeFormat);
    }
    return formatter;
  };

  return (
    <Tooltip
      label={formatTime(time)}
      arrowSize={8}
      hasArrow
      placement="top"
      borderRadius="4px"
    >
      <KText {...props}>
        <TimeAgo
          date={time}
          live={false}
          title={null}
          formatter={ats_enabled ? customFormatter : formatter}
        />
      </KText>
    </Tooltip>
  );
};

export const NumberOfDays = ({ time, ...props }) => {
  const currUser = useSelector(selectUser);
  const { ats_enabled } = useKFlags();
  const { time_fmt, date_fmt, time_zone } = currUser || {};

  if (!time)
    return (
      <KText size="sm-medium" {...props}>
        -
      </KText>
    );

  const givenDate = moment(time, 'YYYY-MM-DD'); // Replace with your date
  const currentDate = moment().tz(time_zone);
  const differenceInDays = currentDate.diff(givenDate, 'days');

  const formatTime = (dateString) => {
    if (ats_enabled && time_fmt && date_fmt) {
      const date = new Date(dateString);
      const timeFormat = time_fmt === 'hour_12' ? 'hh:mm a' : 'HH:mm';
      const dateFormat = getDateFormat(date_fmt);
      return format(date, `${dateFormat} ${timeFormat}`);
    }
    return formatDate(dateString);
  };

  return (
    <Tooltip
      label={formatTime(time)}
      arrowSize={8}
      hasArrow
      placement="top-start"
      borderRadius="4px"
    >
      <Box>
        <KText {...props}>{differenceInDays + ' Days'}</KText>
      </Box>
    </Tooltip>
  );
};
